import { convertUnits, PrintUnits } from '../builderDocument';
import { IPoint } from '../geometry';
import { ICanvasFont } from './canvasFont';
import { IRenderContext, FontUnits } from './renderContext';

/**
 * Accepts a font size as either a number of pixels or points
 * and a line height multiplier as a decimal (e.g. 1.5)
 * and returns the height in number of pixels
 *
 * @param fontSize - font size
 * @param fontSizeUnits - font size units, either 'pt' or 'px'
 * @param lineHeightMultiplier - line height multiplier
 * @returns line height in pixels or pt
 */
export function getLineHeightInPixels(
    fontSize: number,
    fontSizeUnits: 'pt' | 'px',
    lineHeightMultiplier: number,
): number {
    switch (fontSizeUnits) {
        case 'px':
            return fontSize * lineHeightMultiplier;

        case 'pt':
            return convertUnits(fontSize, PrintUnits.points, PrintUnits.pixels) * lineHeightMultiplier;

        default:
            throw new Error(`Unexpected Font Size Units: ${fontSizeUnits}`);
    }
}

/**
 * Accepts a 2d canvas context / render context and function that performs operations on that context
 * and returns a new function that runs the original function in between
 * a context save and context restore so that your mutations on the context
 * do not effect other canvas operations running after your function.
 *
 * @param context - context
 * @param action - action
 * @returns context transaction
 */
export function wrapInContextTransaction<TContext extends CanvasRenderingContext2D | IRenderContext, TResult>(
    context: TContext,
    action: (context: TContext) => TResult,
): () => TResult {
    return () => runInContextTransaction(context, action);
}

/**
 * Accepts a 2d canvas context and function that performs operations on that context
 * and immediately runs the function within a context save and context restore so that
 * your mutations on the context do not effect other canvas operations running after
 * your function.
 *
 * @param context - context
 * @param action - action
 * @returns action(context);
 */
export function runInContextTransaction<TContext extends CanvasRenderingContext2D | IRenderContext, TResult>(
    context: TContext,
    action: (context: TContext) => TResult,
): TResult {
    context.save();
    try {
        return action(context);
    } finally {
        context.restore();
    }
}

export function wrapFontFamilyInQuotes(fontFamily: string): string {
    return (/[",]/g).test(fontFamily) ? fontFamily : `"${fontFamily}"`;
}

export function canvasFontToString(font: ICanvasFont): string {
    const modifiers = [font.style || '', font.variant || '', font.weight || ''];

    const combinedModifiers = modifiers.filter(value => !!value).join(' ');

    const result = `${combinedModifiers} ${font.size}${font.sizeUnit} ${wrapFontFamilyInQuotes(font.family)}`.trim();

    return result;
}

export function drawLine(context: IRenderContext, ...points: IPoint[]): void {
    if (points.length < 1) {
        throw new Error('Line must have at least 2 points');
    }

    context.beginPath().moveTo(points[0]);

    const len = points.length;

    for (let index = 1; index < len; index++) {
        const point = points[index];
        context.lineTo(point);
    }

    context.stroke();
}

export class DomTextMeasurer {
    private readonly dummy: HTMLElement;

    public constructor(createDivElement: () => HTMLDivElement, appendToBody: (element: HTMLElement) => void) {
        this.dummy = createDivElement();
        this.dummy.style.lineHeight = '1';
        this.dummy.style.zIndex = '-9999';
        this.dummy.style.opacity = '0';
        this.dummy.style.position = 'absolute';
        this.dummy.style.top = '0';
        appendToBody(this.dummy);
    }

    public measureTextDimensions(
        text: string,
        fontFamily: string,
        fontSize: number,
        fontUnits: FontUnits,
    ): { height: number; width: number } {
        this.dummy.textContent = text;
        this.dummy.style.fontSize = `${fontSize}${fontUnits}`;
        this.dummy.style.fontFamily = fontFamily;
        return { height: this.dummy.offsetHeight, width: this.dummy.offsetWidth };
    }

    public delete(): void {
        if (this.dummy.parentNode) {
            this.dummy.parentNode.removeChild(this.dummy);
        }
    }
}
